import { useCallback, useEffect, useState } from "react";
import { api, readError } from "./api";
import type { KnowledgeBaseStatus } from "../types";
import { useSseEvent } from "./useSseEvent";

// Live status of the runtime-cloned knowledge base (omni-system-knowledge-base),
// which provides ALL served content. Backs the sidebar "clone & map" control:
// fetches the current status once, then keeps it live over SSE as a clone/refresh
// runs (phase cloning → ready|error). `clone()` triggers a clone/refresh and
// returns an error message on failure, or null on success.
export interface KnowledgeBaseData {
  status: KnowledgeBaseStatus | null;
  loading: boolean;
  refresh: () => Promise<void>;
  clone: () => Promise<string | null>;
}

export function useKnowledgeBase(): KnowledgeBaseData {
  const [status, setStatus] = useState<KnowledgeBaseStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const s = await api.getKnowledgeBase().catch(() => null);
    if (s) setStatus(s);
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useSseEvent<KnowledgeBaseStatus>("knowledge_base_update", (s) => setStatus(s));

  const clone = useCallback(async (): Promise<string | null> => {
    const res = await api.cloneKnowledgeBase().catch(() => null);
    if (!res) return "Network error";
    if (res.status === 202 || res.ok) {
      const body = (await res.json().catch(() => null)) as KnowledgeBaseStatus | null;
      if (body) setStatus(body);
      return null;
    }
    return readError(res, "Clone failed");
  }, []);

  return { status, loading, refresh, clone };
}
